import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Sparkles, RotateCcw } from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { getCountry } from "@/i18n/countries";

/**
 * Rough starter prices per country. Derived from HK$200/mo (yearly = 20%
 * discount vs 12× monthly = HK$1,920). Displayed as placeholders on
 * first open so admins have sensible defaults; not persisted until Save.
 *
 * All amounts are in the country's local currency, rounded to friendly
 * round numbers rather than exact FX conversions.
 */
const SUGGESTED = {
    HK: { currency: "HKD", monthly: 200, yearly: 1920 },
    SG: { currency: "SGD", monthly: 30, yearly: 288 },
    TW: { currency: "TWD", monthly: 750, yearly: 7200 },
    MY: { currency: "MYR", monthly: 120, yearly: 1150 },
    TH: { currency: "THB", monthly: 900, yearly: 8600 },
    PH: { currency: "PHP", monthly: 1400, yearly: 13400 },
    VN: { currency: "VND", monthly: 640000, yearly: 6100000 },
    AE: { currency: "AED", monthly: 95, yearly: 900 },
    AU: { currency: "AUD", monthly: 39, yearly: 375 },
    UK: { currency: "GBP", monthly: 20, yearly: 190 },
    US: { currency: "USD", monthly: 25, yearly: 240 },
    CA: { currency: "CAD", monthly: 35, yearly: 335 },
};

/**
 * Slide-out admin editor for per-country membership pricing.
 *
 * Two-stage workflow (mirrors deal Stripe pricing):
 *  1. Enter monthly + yearly amounts + currency → Save (writes to Mongo)
 *  2. Click Mint per interval → creates the Stripe recurring Price in
 *     that currency, stores its ID back on the country row.
 *
 * Un-minted countries fall back to the HKD `perx_membership_{interval}`
 * price server-side, so unfilled markets keep working.
 */
export default function MembershipPricingDrawer({ open, onOpenChange, country }) {
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mintingInterval, setMintingInterval] = useState(null); // "monthly" | "yearly"
    // Local form state; kept in sync with `row` on load.
    const [form, setForm] = useState({
        currency: "",
        monthly_amount: "",
        yearly_amount: "",
        monthly_stripe_coupon_id: "",
        yearly_stripe_coupon_id: "",
    });

    const suggestion = country ? SUGGESTED[country.code] : null;
    const localMeta = useMemo(
        () => (country ? getCountry(country.code) : null),
        [country]
    );

    useEffect(() => {
        if (!open || !country) return;
        setLoading(true);
        api.get(`/admin/countries/${country.code}/membership-pricing`)
            .then((r) => {
                setRow(r.data);
                    setForm({
                        currency:
                            r.data.currency ||
                            suggestion?.currency ||
                            country.currency ||
                            "HKD",
                        monthly_amount:
                            r.data.monthly_amount != null
                                ? String(r.data.monthly_amount)
                                : "",
                        yearly_amount:
                            r.data.yearly_amount != null
                                ? String(r.data.yearly_amount)
                                : "",
                        monthly_stripe_coupon_id:
                            r.data.monthly_stripe_coupon_id || "",
                        yearly_stripe_coupon_id:
                            r.data.yearly_stripe_coupon_id || "",
                    });
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [open, country]);

    const dirty =
        row &&
        (form.currency !== (row.currency || "") ||
            String(row.monthly_amount ?? "") !== form.monthly_amount ||
            String(row.yearly_amount ?? "") !== form.yearly_amount ||
            (row.monthly_stripe_coupon_id || "") !== form.monthly_stripe_coupon_id ||
            (row.yearly_stripe_coupon_id || "") !== form.yearly_stripe_coupon_id);

    const save = async () => {
        if (!country) return;
        setSaving(true);
        try {
            const payload = { currency: form.currency };
            if (form.monthly_amount !== "")
                payload.monthly_amount = Number(form.monthly_amount);
            if (form.yearly_amount !== "")
                payload.yearly_amount = Number(form.yearly_amount);
            // Send coupon fields whenever they differ from the saved row so
            // clearing (empty string) also propagates. `null` on the input
            // (initial mount) is skipped.
            if ((row?.monthly_stripe_coupon_id || "") !== form.monthly_stripe_coupon_id)
                payload.monthly_stripe_coupon_id = form.monthly_stripe_coupon_id;
            if ((row?.yearly_stripe_coupon_id || "") !== form.yearly_stripe_coupon_id)
                payload.yearly_stripe_coupon_id = form.yearly_stripe_coupon_id;
            const { data } = await api.put(
                `/admin/countries/${country.code}/membership-pricing`,
                payload
            );
            setRow(data);
            toast.success(`${country.code} pricing saved`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const mint = async (interval) => {
        if (!country) return;
        setMintingInterval(interval);
        try {
            const { data } = await api.post(
                `/admin/countries/${country.code}/membership-pricing/mint/${interval}`
            );
            setRow(data);
            toast.success(
                `${country.code} ${interval} Stripe price minted: ${data.currency} ${data[`${interval}_amount`]}`
            );
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setMintingInterval(null);
        }
    };

    const unmint = async (interval) => {
        if (!country) return;
        setMintingInterval(interval);
        try {
            const { data } = await api.delete(
                `/admin/countries/${country.code}/membership-pricing/mint/${interval}`
            );
            setRow(data);
            toast.success(
                `${country.code} ${interval} price deactivated — checkout now falls back to HKD`
            );
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setMintingInterval(null);
        }
    };

    if (!country) return null;

    const isHK = country.code === "HK";

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                data-testid="membership-pricing-drawer"
                className="w-full sm:max-w-2xl bg-cream overflow-y-auto p-0"
            >
                <SheetHeader className="px-8 pt-8 pb-4 border-b border-navy-100 bg-white">
                    <div className="flex items-center gap-3 mb-1">
                        <span className="text-3xl leading-none">
                            {localMeta?.flag}
                        </span>
                        <div>
                            <SheetTitle className="font-display text-2xl text-navy-700">
                                {country.name} membership pricing
                            </SheetTitle>
                            <SheetDescription className="text-navy-500 text-sm">
                                Set the local monthly + yearly amount, then mint each
                                interval on Stripe. Un-minted markets fall back to the
                                shipped HKD price.
                            </SheetDescription>
                        </div>
                    </div>
                </SheetHeader>

                {loading || !row ? (
                    <div className="flex items-center gap-2 text-navy-500 py-16 justify-center">
                        <Loader2 className="animate-spin" size={16} />
                        Loading pricing…
                    </div>
                ) : (
                    <div className="p-8 space-y-6">
                        {isHK && (
                            <div className="rounded-xl border border-gold-200 bg-gold-50 px-4 py-3 text-xs text-navy-600 leading-relaxed">
                                <strong>Hong Kong uses the shipped default prices</strong>{" "}
                                (<span className="font-mono">perx_membership_monthly</span> /
                                <span className="font-mono">_yearly</span>, HKD 200 / 1,920).
                                Existing HK subscribers are on those Price IDs; changing them
                                here would fork the catalogue. Adjust HK only if you know
                                what you&apos;re doing.
                            </div>
                        )}
                        {!isHK && suggestion && !row.updated_at && (
                            <div className="rounded-xl border border-navy-100 bg-white p-4 flex items-start gap-3">
                                <Sparkles
                                    size={16}
                                    className="text-gold-600 flex-shrink-0 mt-0.5"
                                />
                                <div className="text-xs text-navy-500 leading-relaxed">
                                    Suggested starting prices (rounded from HK$200 / HK$1,920
                                    base): <strong>{suggestion.currency} {suggestion.monthly}/mo
                                    · {suggestion.currency} {suggestion.yearly}/yr</strong>.
                                    Tweak below and hit Save.
                                </div>
                            </div>
                        )}

                        {/* Amount form */}
                        <div className="rounded-2xl border border-navy-100 bg-white p-5 space-y-4">
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                        Currency
                                    </Label>
                                    <Input
                                        value={form.currency}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                currency: e.target.value.toUpperCase(),
                                            }))
                                        }
                                        maxLength={4}
                                        data-testid="membership-pricing-currency"
                                        placeholder={suggestion?.currency || country.currency}
                                        className="mt-2 bg-cream border-navy-100 h-11 font-mono uppercase"
                                    />
                                </div>
                                <div>
                                    <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                        Monthly
                                    </Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        step="1"
                                        value={form.monthly_amount}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                monthly_amount: e.target.value,
                                            }))
                                        }
                                        data-testid="membership-pricing-monthly-amount"
                                        placeholder={
                                            suggestion?.monthly?.toString() || "200"
                                        }
                                        className="mt-2 bg-cream border-navy-100 h-11"
                                    />
                                </div>
                                <div>
                                    <Label className="text-navy-700 text-xs uppercase tracking-widest">
                                        Yearly
                                    </Label>
                                    <Input
                                        type="number"
                                        min={0}
                                        step="1"
                                        value={form.yearly_amount}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                yearly_amount: e.target.value,
                                            }))
                                        }
                                        data-testid="membership-pricing-yearly-amount"
                                        placeholder={
                                            suggestion?.yearly?.toString() || "1920"
                                        }
                                        className="mt-2 bg-cream border-navy-100 h-11"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    type="button"
                                    onClick={save}
                                    disabled={saving || !dirty}
                                    data-testid="membership-pricing-save"
                                    className="bg-navy-700 hover:bg-navy-800 text-white rounded-full px-6"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2
                                                size={14}
                                                className="mr-2 animate-spin"
                                            />
                                            Saving…
                                        </>
                                    ) : (
                                        <>
                                            <Save size={14} className="mr-2" />
                                            Save amounts
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>

                        {/* Mint controls per interval */}
                        {["monthly", "yearly"].map((interval) => (
                            <MintRow
                                key={interval}
                                interval={interval}
                                row={row}
                                busy={mintingInterval === interval}
                                onMint={() => mint(interval)}
                                onUnmint={() => unmint(interval)}
                                disabled={isHK}
                                couponValue={form[`${interval}_stripe_coupon_id`]}
                                onCouponChange={(v) =>
                                    setForm((f) => ({
                                        ...f,
                                        [`${interval}_stripe_coupon_id`]: v,
                                    }))
                                }
                            />
                        ))}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

function MintRow({
    interval,
    row,
    busy,
    onMint,
    onUnmint,
    disabled,
    couponValue,
    onCouponChange,
}) {
    const priceId = row[`${interval}_stripe_price_id`];
    const productId = row[`${interval}_stripe_product_id`];
    const savedCoupon = row[`${interval}_stripe_coupon_id`];
    const mintedAt = row[`${interval}_minted_at`];
    const amount = row[`${interval}_amount`];
    const currency = row.currency || "HKD";
    const canMint = !disabled && amount && amount > 0;

    return (
        <div
            data-testid={`membership-mint-${interval}`}
            className="rounded-2xl border border-navy-100 bg-white p-5"
        >
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-1">
                        {interval} · Stripe price
                    </div>
                    <div className="text-navy-700 font-semibold">
                        {amount ? `${currency} ${Number(amount).toLocaleString()}` : "—"}
                    </div>
                </div>
                {priceId ? (
                    <Badge
                        data-testid={`membership-mint-${interval}-status`}
                        className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-[11px] px-2.5 py-1"
                    >
                        Live · {new Date(mintedAt).toLocaleDateString()}
                    </Badge>
                ) : (
                    <Badge
                        data-testid={`membership-mint-${interval}-status`}
                        className="bg-amber-50 text-amber-700 border-amber-200 border text-[11px] px-2.5 py-1"
                    >
                        Not minted
                    </Badge>
                )}
            </div>

            {/* IDs — surface product + price + coupon in a compact triplet */}
            <div className="grid gap-2 mb-3 text-[11px]">
                {productId && (
                    <div className="font-mono text-navy-500 break-all">
                        <span className="text-navy-400 mr-1">product:</span>
                        {productId}
                    </div>
                )}
                {priceId && (
                    <div className="font-mono text-navy-500 break-all">
                        <span className="text-navy-400 mr-1">price:</span>
                        {priceId}
                    </div>
                )}
            </div>

            {/* Coupon input — validated against Stripe on Save */}
            <div className="mb-3">
                <Label className="text-navy-700 text-xs uppercase tracking-widest">
                    Coupon ID{" "}
                    <span className="text-[10px] normal-case text-navy-400 tracking-normal ml-1">
                        (optional · auto-applies at checkout)
                    </span>
                </Label>
                <div className="flex items-center gap-2 mt-2">
                    <Input
                        value={couponValue ?? ""}
                        onChange={(e) => onCouponChange(e.target.value)}
                        placeholder="e.g. launch10, welcome_sg"
                        maxLength={60}
                        disabled={disabled}
                        data-testid={`membership-mint-${interval}-coupon`}
                        className="bg-cream border-navy-100 h-10 font-mono text-xs"
                    />
                    {savedCoupon && savedCoupon === couponValue && (
                        <Badge
                            data-testid={`membership-mint-${interval}-coupon-status`}
                            className="bg-navy-50 text-navy-700 border-navy-200 border text-[10px] px-2 py-1 flex-shrink-0"
                        >
                            Saved
                        </Badge>
                    )}
                </div>
                <p className="text-[10px] text-navy-400 mt-1.5">
                    Create the coupon in Stripe first, then paste its ID here. Save
                    validates it against Stripe live before persisting.
                </p>
            </div>

            <div className="flex gap-2 justify-end">
                {priceId && (
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onUnmint}
                        disabled={busy}
                        data-testid={`membership-mint-${interval}-unmint`}
                        className="text-navy-500 hover:text-red-600 rounded-full h-9 text-xs"
                    >
                        <RotateCcw size={12} className="mr-1.5" />
                        Deactivate
                    </Button>
                )}
                <Button
                    type="button"
                    variant="outline"
                    onClick={onMint}
                    disabled={!canMint || busy}
                    data-testid={`membership-mint-${interval}-btn`}
                    className="rounded-full border-gold-500 text-navy-700 hover:bg-gold-50 h-9 text-xs"
                >
                    {busy ? (
                        <>
                            <Loader2 size={12} className="mr-1.5 animate-spin" />
                            Working…
                        </>
                    ) : priceId ? (
                        "Regenerate"
                    ) : (
                        "Mint Stripe price"
                    )}
                </Button>
            </div>
            {disabled && (
                <p className="text-[11px] text-navy-400 mt-2 italic">
                    HK uses the shipped default Price — locked to avoid forking the
                    catalogue.
                </p>
            )}
        </div>
    );
}
